import React from 'react';
import T from 'prop-types';
import { get, head, has } from 'lodash-es';
import { Legend } from '@piggybank/form';
import {
  Heading,
  Paragraph,
  VisuallyHidden,
  Section,
  PlaybackList,
  PlaybackKey,
  PlaybackValue,
} from '@piggybank/core';
import hooks from 'hooksAlias';
import {
  Translation,
  TranslationScope,
  Optional,
} from 'srcAlias/client/locale';
import { addressFormatter } from 'srcAlias/client/segments/customer/utils/addressFormatter';
import useTrackPageViewAnalytics from 'srcAlias/client/global/customHooks/useTrackPageViewAnalytics';
import { isPrefixSignPresent } from './utils';
import config from 'configAlias';
export const ReviewDetails = ({ segmentName, pageName, customerData }) => {
  useTrackPageViewAnalytics('reviewDetails');

  const fullName = get(customerData, 'individualDetail.name.fullName', '');
  const mobileNoArr = get(customerData, 'mobileTelephoneNumbers', []);
  const mobileNo = head(
    mobileNoArr.map((mobileNoObj) => mobileNoObj.formattedTelephoneNumber)
  );

  const emailArr = get(customerData, 'emailAddresses', []);
  const emailAddress = head(emailArr.map((emailObj) => emailObj.email));
  const address = head(
    get(customerData, 'addresses', {}).filter(
      (addr) => addr.addressIdentifier === 'P1'
    )
  );

  const ReviewDetailsParentComponent = get(
    hooks,
    'customer.reviewDetails.ReviewDetailsParentComponent',
    () => null
  );

  const ReviewDetailsPageNeedsSomethingExpander = get(
    hooks,
    'customer.reviewDetailsExpander.ReviewDetailsPageNeedsSomethingExpander',
    () => null
  );

  const { segments } = config;
  const pageConfigValues = segments
    .find((segment) => segment[0] === 'customer')
    .find((o) => o.pageConfigValues);

  const hasIconSecuritySecure = has(
    hooks,
    'iconsecuritysecure.IconSecuritySecureHook'
  );

  const IconSecuritySecureHook = get(
    hooks,
    'iconsecuritysecure.IconSecuritySecureHook',
    () => null
  );

  return (
    <TranslationScope path={`${segmentName}.${pageName}`}>
      <Heading level={1}>
        <Translation path="heading" />
      </Heading>
      <ReviewDetailsParentComponent>
        <Paragraph lead>
          <Translation path="subHeading" />
        </Paragraph>
        <Heading accentBar level={2}>
          <Translation path="brandBarHeading" />
        </Heading>
        <VisuallyHidden>
          <Legend>
            <Translation path="brandBarHeading" />
          </Legend>
        </VisuallyHidden>
        <Section>
          <PlaybackList
            type={pageConfigValues.pageConfigValues.playBackListType}
          >
            <PlaybackKey>
              {hasIconSecuritySecure && <IconSecuritySecureHook />}
              <Translation path="fullNameLabel" />
            </PlaybackKey>
            <PlaybackValue>{fullName}</PlaybackValue>
            <Optional>
              <PlaybackKey>
                {hasIconSecuritySecure && <IconSecuritySecureHook />}
                <Translation path="passportNo" />
              </PlaybackKey>
              <PlaybackValue>
                {get(
                  customerData,
                  'personalDetails.identificationDocument.documentIdNumber',
                  ''
                )}
              </PlaybackValue>
              <PlaybackKey>
                {hasIconSecuritySecure && <IconSecuritySecureHook />}
                <Translation path="idNo" />
              </PlaybackKey>
              <PlaybackValue>
                {get(
                  customerData,
                  'personalDetails.secondIdentificationDocument.documentIdNumber',
                  ''
                )}
              </PlaybackValue>
            </Optional>
            <PlaybackKey>
              {hasIconSecuritySecure && <IconSecuritySecureHook />}
              <Translation path="mailingAddressLabel" />
            </PlaybackKey>
            <PlaybackValue>{addressFormatter(address)}</PlaybackValue>
            <PlaybackKey>
              {hasIconSecuritySecure && <IconSecuritySecureHook />}
              <Translation path="mobileNoLabel" />
            </PlaybackKey>
            <PlaybackValue>
              {!isPrefixSignPresent(mobileNo) && (
                <Optional>
                  <Translation path="prefixSign" />
                </Optional>
              )}
              {mobileNo}
            </PlaybackValue>
            <PlaybackKey>
              {hasIconSecuritySecure && <IconSecuritySecureHook />}
              <Translation path="emailAddressLabel" />
            </PlaybackKey>
            <PlaybackValue>{emailAddress}</PlaybackValue>
          </PlaybackList>
          <ReviewDetailsPageNeedsSomethingExpander
            startExpanded={pageConfigValues.pageConfigValues.startExpanded}
          />
        </Section>
      </ReviewDetailsParentComponent>
    </TranslationScope>
  );
};

ReviewDetails.propTypes = {
  segmentName: T.string.isRequired,
  pageName: T.string.isRequired,
  customerData: T.object.isRequired,
};
